import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";

export const useSyncStatus = () =>
  useQuery({
    queryKey: ["ingest-status"],
    queryFn: () => api.get("/admin/ingest/status").then((r) => r.data),
    refetchInterval: (q) => (q.state.data?.running ? 3000 : 30000),
    staleTime: 0,
  });

export const SyncNowButton = () => {
  const qc = useQueryClient();
  const { data } = useSyncStatus();
  const wasRunning = useRef(false);
  const running = !!data?.running;

  useEffect(() => {
    if (wasRunning.current && !running) {
      toast.success(`Sync finished · ${data.processed} processed, ${data.failed} failed, ${data.removed} removed`);
      ["sync-runs", "documents", "health"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    }
    wasRunning.current = running;
  }, [running, data, qc]);

  const start = async () => {
    try {
      await api.post("/admin/ingest/s3");
      toast.info("Sync started — scanning S3");
      qc.invalidateQueries({ queryKey: ["ingest-status"] });
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <Button data-testid="header-sync-now-button" onClick={start} disabled={running}
      className="bg-indigo-500 hover:bg-indigo-600 active:scale-95 transition-transform h-9">
      <RefreshCw className={`h-4 w-4 mr-2 ${running ? "animate-spin" : ""}`} />
      <span data-testid="header-sync-status">{running ? `Syncing ${(data.processed ?? 0) + (data.skipped ?? 0) + (data.failed ?? 0)}/${data.total ?? "…"}` : "Sync now"}</span>
    </Button>
  );
};
